<script setup>
import { Handle, Position, useNode } from '@vue-flow/core';
import { useI18n } from 'vue-i18n';
import { useNodeAudience } from '../../composables/useNodeAudience.js';
import { DELAY_UNITS } from '../../constants/nodeTypes.js';

const props = defineProps({
  data: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
});

const { t } = useI18n();
const { id } = useNode();
const audienceCount = useNodeAudience(id);

const timeoutLabel = () => {
  const val = props.data.timeout_value;
  const unit = DELAY_UNITS.find(u => u.key === (props.data.timeout_unit ?? 'hours'));
  if (!val) return null;
  return `${val} ${unit?.label ? t(unit.label) : ''}`;
};
</script>

<template>
  <div
    class="flow-node w-[180px] rounded-xl overflow-hidden shadow-md border-2 transition-all"
    :class="selected ? 'border-slate-400' : 'border-transparent'"
  >
    <Handle type="target" :position="Position.Top" />
    <div class="flex items-center gap-2 px-3 py-2 bg-slate-500 text-white">
      <span class="i-lucide-message-circle-reply size-4 shrink-0" />
      <span class="text-xs font-semibold truncate flex-1">{{ t('FLOW_BUILDER.NODES.WAIT_REPLY.TITLE') }}</span>
      <span v-if="audienceCount !== null" class="text-[9px] bg-white/20 rounded-full px-1.5 py-0.5 font-medium tabular-nums shrink-0">
        {{ audienceCount }}
      </span>
    </div>
    <div class="px-3 py-2 bg-n-solid-1 text-n-slate-12 text-xs">
      <span v-if="timeoutLabel()">{{ t('FLOW_BUILDER.NODES.WAIT_REPLY.TIMEOUT', { value: timeoutLabel() }) }}</span>
      <span v-else class="text-n-slate-9">{{ t('FLOW_BUILDER.NODES.WAIT_REPLY.TIMEOUT_NOT_SET') }}</span>
    </div>
    <div class="bg-n-solid-1 flex justify-between px-[18%] pt-1 pb-3 rounded-b-xl">
      <span class="text-[10px] text-n-slate-9">{{ t('FLOW_BUILDER.NODES.WAIT_REPLY.REPLIED') }}</span>
      <span class="text-[10px] text-n-slate-9">{{ t('FLOW_BUILDER.NODES.WAIT_REPLY.TIMEOUT_LABEL') }}</span>
    </div>
    <Handle
      id="replied"
      type="source"
      :position="Position.Bottom"
      class="!bg-slate-500 !border-slate-400"
      style="left: 25%"
    />
    <Handle
      id="timeout"
      type="source"
      :position="Position.Bottom"
      class="!bg-n-ruby-9 !border-n-ruby-7"
      style="left: 75%"
    />
  </div>
</template>
